import { useState } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import PortfolioCustomerListDialog from "@/v2-backoffice-client/portfolio/PortfolioCustomerListDialog"
import type { ColumnMetaMap } from "@/v2-backoffice-client/components/table"
import {
  createCustomerColumns,
  customerColumns,
  type PortfolioCustomerRow,
} from "@/v2-backoffice-client/portfolio/portfolio.customerTable.columns"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  formatAppArea,
  formatAppCode,
  formatTrendDetail,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import type { PortfolioAreaIntel } from "@/v2-backoffice-client/portfolio/portfolio.area.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioAreaListProps = {
  /** Every area with volume, biggest first (deriveAreaIntelligence). */
  areas: PortfolioAreaIntel[]
  /** Scoped customers with tracked activity, the adopter denominator. */
  activeCustomerCount: number
  /** The fleet's monthly actives, the user-adoption denominator. */
  activeUserCount: number
}

type AreaDialog = {
  area: PortfolioAreaIntel
  kind: "adopters" | "whitespace"
}
type AdopterRow = { customer: PortfolioCustomerSummary; volume: number }

const ADOPTER_CUSTOMER = customerColumns<AdopterRow>()

/** Customers using a capability: volume in it first, then the money. */
const ADOPTER_COLUMN_METAS: ColumnMetaMap<AdopterRow> = {
  name: ADOPTER_CUSTOMER.name,
  churn: ADOPTER_CUSTOMER.churn,
  volume: {
    id: "volume",
    label: "In this area, 28 d",
    description: "Weighted actions in this area over the last 28 days.",
    getValue: (row) => row.volume,
    defaultSize: 140,
    className: "tabular-nums",
    renderCell: ({ row }) => formatNumber(Math.round(row.original.volume)),
  },
  billed: ADOPTER_CUSTOMER.billed,
  seats: ADOPTER_CUSTOMER.seats,
}
const ADOPTER_COLUMNS = createCustomerColumns<AdopterRow>(ADOPTER_COLUMN_METAS)

const WHITESPACE_CUSTOMER = customerColumns<PortfolioCustomerRow>()

/** Active customers not using a capability: the money first. */
const WHITESPACE_COLUMN_METAS: ColumnMetaMap<PortfolioCustomerRow> = {
  name: WHITESPACE_CUSTOMER.name,
  churn: WHITESPACE_CUSTOMER.churn,
  billed: WHITESPACE_CUSTOMER.billed,
  seats: WHITESPACE_CUSTOMER.seats,
  score: WHITESPACE_CUSTOMER.score,
}
const WHITESPACE_COLUMNS = createCustomerColumns<PortfolioCustomerRow>(
  WHITESPACE_COLUMN_METAS,
)

// The rows of the area intelligence card and the two lists behind their
// counts. Owns which list is open, so opening one does not re-derive the
// areas in the section above.
function PortfolioAreaList(props: PortfolioAreaListProps) {
  const { areas, activeCustomerCount, activeUserCount } = props

  const [dialog, setDialog] = useState<AreaDialog | null>(null)

  const maxVolume = Math.max(
    1,
    ...areas.map((area) => Math.max(area.current, area.previous)),
  )

  return (
    <>
      {areas.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No interest-area activity in the last 28 days.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          <li className="usage-report-list-head gap-3" aria-hidden>
            <span className="w-44 shrink-0">Capability</span>
            <span className="min-w-0 flex-1">
              Weighted volume, last 28 d (previous 28 in grey)
            </span>
            <span className="w-24 shrink-0 text-center">Daily</span>
            <span className="w-16 shrink-0 text-right">28 d</span>
            <span className="w-16 shrink-0 text-center">Trend</span>
            <span className="w-24 shrink-0 text-right">Of users</span>
            <span className="w-36 shrink-0 text-right">Using · billed</span>
            <span className="w-36 shrink-0 text-right">Not using · to win</span>
          </li>
          {areas.map((area) => (
            <li key={area.area} className="flex items-center gap-3">
              <span className="w-44 shrink-0 truncate text-xs">
                {formatAppArea(area.area)}
              </span>

              {/* Current window split by product over a ghost of the
              previous window, same scale — the gap IS the trend and the
              amber share IS the migration exposure. */}
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex h-2 overflow-hidden rounded-full bg-muted">
                  {area.appSplit.map((split) => (
                    <span
                      key={split.app}
                      className="block h-full"
                      title={`${formatAppCode(split.app)} · ${formatNumber(Math.round(split.current))} weighted actions`}
                      style={{
                        width: `${(split.current / maxVolume) * 100}%`,
                        backgroundColor: getAppCodeColor(split.app),
                      }}
                    />
                  ))}
                </span>
                <span className="h-1 overflow-hidden rounded-full">
                  <span
                    className="block h-full rounded-full bg-muted-foreground/30"
                    style={{
                      width: `${(area.previous / maxVolume) * 100}%`,
                    }}
                  />
                </span>
              </span>

              <span
                className="w-24 shrink-0"
                title="Daily weighted volume, last 28 days"
              >
                <UsageSparkline series={area.daily} />
              </span>

              <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {formatNumber(Math.round(area.current))}
              </span>

              <UsageTrendBadge
                trendPct={area.trendPct}
                detail={formatTrendDetail(area.current, area.previous)}
                className="w-16 shrink-0 justify-center"
              />

              {/* Feature adoption at USER level (the customer counts to the
              right are account-level): share of monthly actives with
              this area among their top areas. */}
              <span
                className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
                title={`${formatNumber(area.userAdopters)} of ${formatNumber(activeUserCount)} monthly-active users have this among their top areas — a floor, since profiles carry each user's top 3 areas only`}
              >
                {activeUserCount > 0
                  ? `${formatNumber(Math.round((area.userAdopters / activeUserCount) * 100))}% of users`
                  : "—"}
              </span>

              {/* Counts say who, the money says what it's worth: billed 12-mo
              revenue of the customers behind each list. */}
              <button
                type="button"
                className="w-36 shrink-0 text-right text-xs tabular-nums text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                title="Customers with tracked use of this area, and their combined 12-month billed revenue — click for the full list"
                onClick={() => setDialog({ area, kind: "adopters" })}
              >
                {formatNumber(area.adopters.length)}/
                {formatNumber(activeCustomerCount)}
                {area.adopterBilled12M > 0 && (
                  <>
                    {" · "}
                    {formatCompactAmount(area.adopterBilled12M, area.currency)}
                  </>
                )}
              </button>

              <button
                type="button"
                className="w-36 shrink-0 text-right text-xs tabular-nums text-amber-500/90 underline-offset-2 hover:text-amber-400 hover:underline"
                title="ACTIVE customers with no tracked use of this area — the expansion campaign list, worth their combined 12-month billed revenue"
                onClick={() => setDialog({ area, kind: "whitespace" })}
              >
                {formatNumber(area.whitespace.length)}
                {area.whitespaceBilled12M > 0 ? (
                  <>
                    {" · "}
                    {formatCompactAmount(
                      area.whitespaceBilled12M,
                      area.currency,
                    )}
                  </>
                ) : (
                  " · —"
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {dialog != null && dialog.kind === "adopters" && (
        <PortfolioCustomerListDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialog(null)
          }}
          title={`${formatAppArea(dialog.area.area)} — ${formatNumber(dialog.area.adopters.length)} customers using it`}
          description="Heaviest use first — the anchor accounts for references, case studies and webinar invites. Volume is their weighted activity in this area over the last 28 days; billed is their 12-month invoice total."
          tableId="portfolio-area-adopters-dialog"
          rows={dialog.area.adopters}
          columns={ADOPTER_COLUMNS}
          initialSorting={[{ id: "volume", desc: true }]}
        />
      )}

      {dialog != null && dialog.kind === "whitespace" && (
        <PortfolioCustomerListDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialog(null)
          }}
          title={`${formatAppArea(dialog.area.area)} — ${formatNumber(dialog.area.whitespace.length)} active customers not using it`}
          description="Accounts with tracked activity elsewhere but none in this area — the expansion campaign list. Biggest books first: money already paying is the strongest signal a demo here lands."
          tableId="portfolio-area-whitespace-dialog"
          rows={dialog.area.whitespace.map((customer) => ({ customer }))}
          columns={WHITESPACE_COLUMNS}
          initialSorting={[{ id: "billed12M", desc: true }]}
        />
      )}
    </>
  )
}

export default PortfolioAreaList
